import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from 'react-router-dom';
import { Box, Grid, } from "@mui/material";
import { useTranslation } from "react-i18next";
import DataTable from "../../components/common/DataTable";
import { AuthContext } from "../../context/AuthContext";
import { GET_FREE_DAYS_REPORT } from "../../services/apiRoutes";
import CustomReport from "../common/CustomReport";
import { getSuppliersList, getColorById, getColorPlate, } from "../../services/CommonServices";
import { genericPost } from "../../services/authServices";
import CustomTextField from "../common/CustomTextField";
import CustomFilter from "../common/CustomFilter";
import CustomButton from "../common/CustomButton";
import FindReplaceOutlinedIcon from '@mui/icons-material/FindReplaceOutlined';
import { ClearRounded, } from "@mui/icons-material";
import CustomAutocomplete from "../common/CustomAutoComplete";
import CustomStatus from "../common/CustomStatus";
import { useCompanyDateFormat } from "../../constants/useCompanyDateFormat";
import CustomDataTable from "../common/CustomDataTable";
import { AccessDenied, hasCreatePermission, hasDeletePermission, hasEditPermission, hasViewPermission } from "../../services/CommonServices";
import { useLocation } from "react-router-dom";


const title = "Free Days Report";

const FreeDaysReport = () => {
    const companyDateFormat = useCompanyDateFormat();
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [permission, setPermissions] = useState()
    const [data, setData] = useState([]);
    const { token } = useContext(AuthContext)
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const filterOpen = () => setOpenDrawer(true);
    const filterClose = () => setOpenDrawer(false);
    const [openDrawer, setOpenDrawer] = useState(false);
    const [suppliersList, setSuppliersList] = useState([]);
    const [page, setPage] = useState(0); // UI 0-based
    const [pageSize, setPageSize] = useState(10);
    const [rowCount, setRowCount] = useState(0);
    const [colorlist, setColorlist] = useState([]);

    const [filterPayload, setFilterPayload] = useState({
        shipmentId: '',
        clientCompanyId: '',
        fromDate: '',
        toDate: '',

    });
    const location = useLocation();
    const currentPath = location.pathname;
    useEffect(() => {
        let rolePermisionsData = JSON.parse(sessionStorage.getItem('userData')) || []
        let editPermision = hasEditPermission(currentPath, rolePermisionsData?.permissions)
        let deletePermission = hasDeletePermission(currentPath, rolePermisionsData?.permissions)
        let createPermision = hasCreatePermission(currentPath, rolePermisionsData?.permissions)
        let viewPermision = hasViewPermission(currentPath, rolePermisionsData?.permissions)

        setPermissions({
            edit: editPermision,
            delete: deletePermission,
            create: createPermision,
            view: viewPermision,
        })
    }, [])
    const handleFilterChange = (field, value) => {
        setFilterPayload((prev) => ({
            ...prev,
            [field]: value || '',
        }));
    };
    const handleClearFilters = useMemo(() => {
        return () => {
            setFilterPayload({
                shipmentId: '',
                clientCompanyId: '',
                fromDate: '',
                toDate: '',
            });
            fetchData(0, pageSize, '');
        };
    }, [pageSize]);
    useEffect(() => {
        getCommonData();
    }, []);

    const getCommonData = async () => {
        const suppliersListData = await getSuppliersList();
        setSuppliersList(suppliersListData);
        const colorData = await getColorPlate();
        setColorlist(colorData);

    }

    const columns = useMemo(
        () => [
            { accessorKey: "index", header: "SN.", size: 50, tableHeader: { alignItems: 'center', }, tableStyle: { textAlign: "center" }, enableColumnFilter: false },
            { accessorKey: 'shipmentDate', header: 'Date', size: 50, tableStyle: { textAlign: "center" }, },
            { accessorKey: 'shipmentId', header: 'Shipment ID', size: 50, tableStyle: { textAlign: "center" }, },
            {
                accessorKey: 'bookingNumber',
                header: 'Booking Number',
                size: 80,
                tableStyle: {
                    textAlign: "center",
                },
            },
            {
                accessorKey: 'clientCompanyName', header: 'Supplier Name', size: 50, tableHeader: {
                    justifyContent: 'flex-start',
                },
            },
            {
                accessorKey: "country",
                header: "Country",
                tableStyle: { textAlign: "center" },
                size: 50,
                Cell: ({ row }) => {
                    return (
                        <CustomStatus
                            title={row.original.country}
                            sx={{
                                backgroundColor: row.original.countryIdBackground,
                                color: row.original.countryIdColor,
                                border: `1px solid ${row.original.countryIdColor}`,
                                textAlign: "center",
                                display: "inline-block",
                            }}
                        />
                    );
                },
                enableSorting: false,
            },
            { accessorKey: 'numberOfContainers', header: 'No. of Containers', size: 50, tableStyle: { textAlign: "center" }, },
            { accessorKey: 'freeDaysAtDestination', header: 'Free days', size: 50, tableStyle: { textAlign: "center" }, },
            { accessorKey: 'dueDate', header: 'Due Date', size: 50, tableStyle: { textAlign: "center" }, },

            {
                accessorKey: "balanceDays",
                header: "Balance Days",
                tableStyle: { textAlign: "center" },
                size: 50,
                Cell: ({ row }) => {
                    const { balanceDays, balanceDaysBackground, balanceDaysColor } = row.original;
                    if (!balanceDays) return null;
                    return (
                        <CustomStatus
                            title={row.original.balanceDays}
                            sx={{
                                backgroundColor: row.original.balanceDaysBackground,
                                color: row.original.balanceDaysColor,
                                border: `1px solid ${row.original.balanceDaysColor}`,
                                textAlign: "center",
                                display: "inline-block",
                            }}
                        />
                    );
                },
                enableSorting: false,
            },
        ],
        [permission]
    );

    const fetchData = async (pageIndex = 0, pageSize = 10,) => {
        setLoading(true);
        try {
            const payload = {
                page: pageIndex + 1,
                limit: pageSize,
                ...filterPayload
            };
            const response = await genericPost(GET_FREE_DAYS_REPORT, payload, {});
            if (response.data.success && response.data.shippingBooking) {
                const shippingBookingData = response.data.shippingBooking.map((booking, index) => {
                    const etaDate = new Date(booking.etaDate);
                    etaDate.setDate(etaDate.getDate() + 14);
                    const trackerDateStr = booking.shippingTrackersr?.[0]?.date || '';
                    const freeDaysAtDestination = Number(booking.freeDaysAtDestination || 0);
                    const balanceDateRaw = booking.shippingTrackersrBalaceDate?.[0]?.date || "";
                    const balanceDateFormatted = balanceDateRaw ? balanceDateRaw.split("T")[0] : "";
                    let dueDateFormatted = '';
                    let balanceDays = '';
                    let status = '';
                    let balanceDaysBackground = '';
                    let balanceDaysColor = '';
                    if (trackerDateStr && freeDaysAtDestination) {
                        const trackerDate = new Date(trackerDateStr);
                        trackerDate.setDate(trackerDate.getDate() + freeDaysAtDestination);
                        dueDateFormatted = trackerDate.toISOString().split('T')[0];
                        const currentDate = new Date();
                        const dueDate = new Date(dueDateFormatted);
                        const timeDiff = dueDate.getTime() - currentDate.getTime();
                        const diffDays = Math.ceil(timeDiff / (1000 * 3600 * 24));
                        if (diffDays > 0) {
                            balanceDays = diffDays;
                            status = `Remaining ${diffDays} days`;
                            balanceDaysBackground = '#f0ecff';
                            balanceDaysColor = '#0e0047';
                        } else if (diffDays < 0) {
                            balanceDays = Math.abs(diffDays);
                            status = `Overdue by ${Math.abs(diffDays)} days`;
                            balanceDaysBackground = '#FFD7E9';
                            balanceDaysColor = '#41001d';
                        } else {
                            balanceDays = 0;
                            status = 'Due Today';
                            balanceDaysBackground = '#e9ffe3';
                            balanceDaysColor = '#0a2d00';
                        }
                    }
                    const countryColorObj = getColorById(booking?.clientCompany?.billingCountry?.id, colorlist, {
                        matchKey: "colorId",
                        prefixLength: 1,
                    });
                    return {
                        index: (index + 1).toString().padStart(2, '0'),
                        shippingBookingId: booking.shippingBookingId || '',
                        freeDaysAtDestination: booking.freeDaysAtDestination || '',
                        bookingNumber: booking?.bookingNumber || "",
                        numberOfContainers: booking.numberOfContainers || '',
                        portOfLoading: booking.portOfLoading || '',
                        portOfDestination: booking.portOfDestination || '',
                        shipmentId: booking.shipmentId || '',
                        etdDate: booking.etdDate || '',
                        destinationCharges: booking.destinationCharges || '',
                        shippingLineCharges: booking.shippingLineCharges || '',
                        clientCompanyName: booking.clientCompany?.clientCompanyName || '',
                        country: booking.clientCompany?.billingCountry?.name || '',
                        blCharges: booking.blCharges || '',
                        cifFobName: booking.cifFob?.cifFobName || '',
                        shippingLineName: booking.shippingLines?.shippingLineName || '',
                        agencyName: booking.freightForwardAgency?.agencyName || '',
                        chaName: booking.customClearingAgency?.chaName || '',
                        shipmentDate: booking.importShipment?.shipmentDate || "",
                        dueDate: companyDateFormat(dueDateFormatted) || '',
                        shipmentDate: companyDateFormat(booking.importShipment?.shipmentDate) || '',
                        balanceDaysBackground,
                        balanceDaysColor,
                        balanceDays: !balanceDateFormatted ? status : "Day Complete",
                        countryIdColor: countryColorObj.color,
                        countryIdBackground: countryColorObj.backgroundColor,
                    };
                });
                setData(shippingBookingData);
                setRowCount(response.data.pagination?.totalRecords || 0);
            } else {
                setError('No shipping bookings found');
            }
        } catch (error) {
            setError('Error fetching shipping bookings');
            console.error('Error fetching shipping bookings', error);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        fetchData(page, pageSize)
    }, [token, page, pageSize, colorlist])

    const mainContent = (
        <CustomDataTable
            title={title}
            columns={columns}
            data={data}
            handleFilterButton={filterOpen}
            fiterButtonTitile="Fiter"
            rowCount={rowCount}
            page={page}
            setPage={setPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            fromDate={filterPayload.fromDate || ""}
            toDate={filterPayload.toDate || ""}
        />
    );
    const defaultView = 'freedaysreport';

    const getMainContent = (mainContent, permission) => {
        return permission?.view ? mainContent : null;
    };

    return (
        <>
            <CustomReport
                defaultView={defaultView}
                mainContent={getMainContent(mainContent, permission)}
            />
            {!permission?.view && <AccessDenied />}

            <CustomFilter
                drawerTitle="Filter Options"
                drawerWidth={300}
                openDrawer={openDrawer}
                onClose={filterClose}
                drawerContent={
                    <>
                        <Box>
                            <Grid container spacing={1} sx={{ padding: 0, }}>
                                <Grid item xs={12} sm={12} md={12}>
                                    <CustomTextField
                                        type="text"
                                        placeholder="Enter Shipment ID"
                                        value={filterPayload.shipmentId}
                                        onChange={(e) => handleFilterChange('shipmentId', e.target.value)}

                                    />
                                </Grid>
                                <Grid item xs={12} sm={12} md={12}>
                                    <CustomAutocomplete
                                        options={suppliersList}
                                        getOptionLabel={(option) => option.title}
                                        placeholder="Select Supplier"
                                        onChange={(event, newValue) => handleFilterChange("clientCompanyId", newValue?.id)}
                                        value={suppliersList.find((item) => item.id === filterPayload.clientCompanyId) || null}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={12} md={12}>
                                    <CustomTextField
                                        fieldLabel="from Date"
                                        type="date"
                                        value={filterPayload.fromDate}
                                        onChange={(e) => handleFilterChange('fromDate', e.target.value)}
                                        placeholder="From Date"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={12} md={12}>
                                    <CustomTextField
                                        fieldLabel="to Date"
                                        type="date"
                                        value={filterPayload.toDate}
                                        onChange={(e) => handleFilterChange('toDate', e.target.value)}
                                        placeholder="To Date"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={12} md={12} sx={{ display: 'flex', alignItems: 'center', marginTop: "10px" }}>
                                    <CustomButton
                                        startIcon={<FindReplaceOutlinedIcon />}
                                        title="Apply"
                                        size="small"
                                        variant="contained"
                                        onClick={fetchData}
                                    />
                                    <CustomButton
                                        variant="outlined"
                                        title="Clear"
                                        startIcon={<ClearRounded />}
                                        onClick={handleClearFilters}
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    </>
                }
            />
        </>
    );
};
export default FreeDaysReport;
